import { useEffect, useMemo, useRef, useState } from "react";
import { questionBank, modules } from "@/data/questionBank";
import type { AnswerValue, Answers, Question } from "@/lib/cvcs-types";
import {
  answeredCount,
  dimensionMetas,
  likertLabels,
  scoreQuestionnaire,
  scoredQuestionCount,
} from "@/lib/scoring";
import type { ContributionProfile, GlobalAverage } from "@/lib/global-average-types";
import "./index.css";

const STORAGE_KEY = "cvcs-progress-v1";
const CONTRIB_KEY = "cvcs-contributed-v1";
const RESPONDENT_KEY = "cvcs-respondent-id";

const uuidv4 = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const bytes = (typeof crypto !== "undefined" ? crypto.getRandomValues(new Uint8Array(16)) : null) ?? (() => {
    const fallback = new Uint8Array(16);
    for (let i = 0; i < 16; i++) fallback[i] = Math.floor(Math.random() * 256);
    return fallback;
  })();
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const loadOrCreateRespondentId = (): string => {
  if (typeof window === "undefined") return "pending";
  try {
    const existing = localStorage.getItem(RESPONDENT_KEY);
    if (existing && existing.length >= 8) return existing;
    const fresh = uuidv4();
    localStorage.setItem(RESPONDENT_KEY, fresh);
    return fresh;
  } catch {
    return uuidv4();
  }
};

type ContribState = { anonymous: boolean; full: boolean };

const loadContribState = (): ContribState => {
  if (typeof window === "undefined") return { anonymous: false, full: false };
  try {
    const raw = localStorage.getItem(CONTRIB_KEY);
    if (!raw) return { anonymous: false, full: false };
    const parsed = JSON.parse(raw) as Partial<ContribState>;
    return { anonymous: Boolean(parsed.anonymous), full: Boolean(parsed.full) };
  } catch {
    return { anonymous: false, full: false };
  }
};

const saveContribState = (state: ContribState): void => {
  if (typeof window === "undefined") return;
  localStorage.setItem(CONTRIB_KEY, JSON.stringify(state));
};

const profileToContribution = (profile: ReturnType<typeof scoreQuestionnaire>): ContributionProfile => ({
  baseline: Object.fromEntries(dimensionMetas.map(d => [d.code, profile.baseline[d.code].score])),
  values: Object.fromEntries(Object.entries(profile.values).map(([code, axis]) => [code, axis.score])),
  stressDeltas: profile.stressDeltas as Record<string, number>,
  groupedStress: profile.groupedStress,
  topIdeology: profile.ideologyMatches[0]?.name,
});

const moduleOrder = ["Warm-up", ...modules];
const questionById = new Map(questionBank.map(q => [q.id, q]));

function shuffle<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j]!, copy[i]!];
  }
  return copy;
}

function buildFlow(): string[] {
  const warmup = questionBank.filter(q => q.module === "Warm-up").map(q => q.id);
  const middle = modules
    .filter(module => module !== "Stress-test vignettes")
    .flatMap(module => {
      const questions = questionBank.filter(q => q.module === module && q.type !== "importance");
      const imp = questionBank.find(q => q.type === "importance" && q.targetModule === module);
      return [...shuffle(questions).map(q => q.id), ...(imp ? [imp.id] : [])];
    });
  const stressQuestions = questionBank.filter(q => q.module === "Stress-test vignettes" && q.type !== "importance");
  const stressImportance = questionBank.find(q => q.type === "importance" && q.targetModule === "Stress-test vignettes");
  return [...warmup, ...middle, ...shuffle(stressQuestions).map(q => q.id), ...(stressImportance ? [stressImportance.id] : [])];
}

function loadProgress() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { answers: Answers; flowIds: string[]; index: number };
    if (!Array.isArray(parsed.flowIds) || parsed.flowIds.some(id => !questionById.has(id))) return null;
    return parsed;
  } catch {
    return null;
  }
}

type View = "landing" | "questionnaire" | "results" | "methodology" | "privacy";

export function App() {
  const saved = useMemo(loadProgress, []);
  const [view, setView] = useState<View>(saved ? "questionnaire" : "landing");
  const [flowIds, setFlowIds] = useState<string[]>(saved?.flowIds ?? buildFlow());
  const [answers, setAnswers] = useState<Answers>(saved?.answers ?? {});
  const [index, setIndex] = useState(saved?.index ?? 0);

  const flow = useMemo(() => flowIds.map(id => questionById.get(id)).filter(Boolean) as Question[], [flowIds]);
  const current = flow[index] ?? flow[0];
  const profile = useMemo(() => scoreQuestionnaire(questionBank, answers), [answers]);
  const progress = Math.round(((index + 1) / flow.length) * 100);
  const scoredAnswered = answeredCount(questionBank, answers);
  const scoredTotal = scoredQuestionCount(questionBank);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ answers, flowIds, index }));
  }, [answers, flowIds, index]);

  const start = () => {
    const fresh = buildFlow();
    setFlowIds(fresh);
    setAnswers({});
    setIndex(0);
    setView("questionnaire");
  };

  const reset = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(CONTRIB_KEY);
    setFlowIds(buildFlow());
    setAnswers({});
    setIndex(0);
    setView("landing");
  };

  const setAnswer = (id: string, value: AnswerValue) => setAnswers(prev => ({ ...prev, [id]: value }));

  const next = () => {
    if (index >= flow.length - 1) setView("results");
    else setIndex(i => Math.min(i + 1, flow.length - 1));
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 border-b border-[var(--line-soft)] bg-[var(--paper)]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <button onClick={() => setView("landing")} className="group flex items-baseline gap-3 text-left focus-ring rounded-md">
            <span className="serif text-[22px] leading-none" style={{ fontWeight: 500 }}>CVCS</span>
            <span className="hidden text-[12px] text-[var(--ink-muted)] sm:inline">Civic value pressure study</span>
          </button>
          <nav className="flex items-center gap-1 text-[14px]">
            <button className="nav-link" onClick={() => setView("methodology")}>Method</button>
            {scoredAnswered > 0 && <button className="nav-link" onClick={() => setView("results")}>Results</button>}
            <button className="nav-cta ml-2" onClick={start}>Start</button>
          </nav>
        </div>
      </header>

      {view === "landing" && (
        <Landing
          onStart={start}
          onMethod={() => setView("methodology")}
          userProfile={scoredAnswered > 0 ? profile : null}
          userAnsweredScored={scoredAnswered}
          userScoredTotal={scoredTotal}
        />
      )}
      {view === "questionnaire" && current && (
        <Questionnaire
          question={current}
          answer={answers[current.id]}
          onAnswer={value => setAnswer(current.id, value)}
          onNext={next}
          onBack={() => setIndex(i => Math.max(0, i - 1))}
          index={index}
          total={flow.length}
          progress={progress}
          modulePosition={moduleOrder.indexOf(current.module) + 1}
          canBack={index > 0}
          onReset={reset}
        />
      )}
      {view === "results" && (
        <Results
          profile={profile}
          answers={answers}
          scoredAnswered={scoredAnswered}
          scoredTotal={scoredTotal}
          onRestart={start}
          contribution={profileToContribution(profile)}
        />
      )}
      {view === "methodology" && <Methodology onStart={start} />}
      {view === "privacy" && <Privacy onBack={() => setView("landing")} />}

      <SiteFooter onNavigate={setView} />
    </div>
  );
}

function SiteFooter({ onNavigate }: { onNavigate: (v: View) => void }) {
  return (
    <footer className="mx-auto max-w-6xl px-5 pb-16 pt-12 sm:px-8">
      <div className="divider mb-10" />
      <div className="grid gap-10 sm:grid-cols-[1.4fr_1fr_1fr]">
        <div>
          <p className="display text-[18px]" style={{ fontWeight: 500 }}>CVCS</p>
          <p className="mt-3 max-w-sm text-[14px] leading-[1.65] text-[var(--ink-muted)]">
            A reflective instrument that maps how your civic instincts move under pressure. Educational, not diagnostic.
          </p>
        </div>
        <div>
          <p className="eyebrow mb-4">Pages</p>
          <ul className="grid gap-2 text-[14px]">
            <li><button onClick={() => onNavigate("landing")} className="text-[var(--ink-soft)] hover:text-[var(--ink)]">Home</button></li>
            <li><button onClick={() => onNavigate("methodology")} className="text-[var(--ink-soft)] hover:text-[var(--ink)]">Methodology</button></li>
            <li><button onClick={() => onNavigate("privacy")} className="text-[var(--ink-soft)] hover:text-[var(--ink)]">Privacy</button></li>
          </ul>
        </div>
        <div>
          <p className="eyebrow mb-4">Data</p>
          <p className="text-[13px] leading-[1.7] text-[var(--ink-muted)]">
            Only anonymized aggregate scores feed the Global Average. No accounts, no tracking, no personal identifiers.
          </p>
        </div>
      </div>
      <div className="mt-10 flex flex-wrap items-center justify-between gap-3 text-[12px] text-[var(--ink-subtle)]">
        <span>v0.1 · research preview</span>
        <span>© {new Date().getFullYear()} CVCS</span>
      </div>
    </footer>
  );
}

function Landing({
  onStart,
  onMethod,
  userProfile,
  userAnsweredScored,
  userScoredTotal,
}: {
  onStart: () => void;
  onMethod: () => void;
  userProfile: ReturnType<typeof scoreQuestionnaire> | null;
  userAnsweredScored: number;
  userScoredTotal: number;
}) {
  const hasUserProfile = userProfile !== null && userAnsweredScored >= Math.floor(userScoredTotal * 0.5);
  return (
    <main className="mx-auto max-w-6xl px-5 sm:px-8">
      <section className="grid gap-12 py-16 sm:py-24 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16 lg:py-32">
        <div className="flex flex-col justify-center">
          <h1 className="serif text-[44px] leading-[1.02] sm:text-[64px] lg:text-[76px]" style={{ fontWeight: 400, letterSpacing: "-0.025em" }}>
            What changes when politics stops being calm?
          </h1>
          <p className="mt-8 max-w-xl text-[18px] leading-[1.65] text-[var(--ink-soft)]">
            CVCS maps your baseline civic instincts, then watches how they move under pressure — scarcity, corruption, threat, disorder, uncertainty, and cost. The result is a profile, not a label.
          </p>
          <div className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <button className="primary-button" onClick={onStart}>Start the questionnaire</button>
            <button className="ghost-link" onClick={onMethod}>Read the method →</button>
          </div>
          <p className="mt-8 max-w-lg text-[14px] leading-[1.65] text-[var(--ink-muted)]">
            CVCS is educational and reflective. It is not a clinical or scientific diagnosis. The methodology page is candid about its limits and validation plan.
          </p>
        </div>

        <PressurePreview />
      </section>

      <section className="border-t border-[var(--line-soft)] py-20">
        <GlobalAverageView userProfile={hasUserProfile ? userProfile : null} />
      </section>
    </main>
  );
}

function GlobalAverageView({ userProfile }: { userProfile: ReturnType<typeof scoreQuestionnaire> | null }) {
  const [average, setAverage] = useState<GlobalAverage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/global-average")
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: GlobalAverage) => { if (!cancelled) setAverage(data); })
      .catch(err => { if (!cancelled) setError(String(err)); });
    return () => { cancelled = true; };
  }, []);

  const dimensions = dimensionMetas;
  const userCount = userProfile ? "your profile" : "no profile yet";

  return (
    <div className="grid gap-8">
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <p className="eyebrow">Global average</p>
          <h2 className="display mt-3 text-[36px] leading-[1.1] sm:text-[44px]">Where everyone tends to land.</h2>
        </div>
        <div className="text-right">
          {average === null && !error && <p className="text-[13px] text-[var(--ink-muted)]">loading…</p>}
          {error && <p className="text-[13px] text-[var(--clay)]">global average unavailable</p>}
          {average && (
            <p className="text-[13px] text-[var(--ink-muted)]">
              {average.count === 0
                ? "no respondents yet · be the first"
                : `${average.count} respondent${average.count === 1 ? "" : "s"} · ${userCount}`}
            </p>
          )}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-5 text-[12px] text-[var(--ink-muted)]">
        <span className="inline-flex items-center gap-2">
          <span className="inline-block h-[10px] w-[10px] rounded-full bg-[var(--ink)]" />
          global average
        </span>
        {userProfile && (
          <span className="inline-flex items-center gap-2">
            <span className="inline-block h-[10px] w-[10px] rounded-full bg-[var(--clay)] ring-2 ring-[var(--paper)]" />
            you
          </span>
        )}
      </div>

      <div className="mt-2 grid gap-3 md:grid-cols-2 md:gap-x-10">
        {dimensions.map(d => {
          const globalValue = average?.dimensions[d.code];
          const userValue = userProfile?.baseline[d.code]?.score;
          return (
            <GlobalAverageRow
              key={d.code}
              label={d.name}
              global={typeof globalValue === "number" ? globalValue : null}
              user={typeof userValue === "number" ? userValue : null}
            />
          );
        })}
      </div>
    </div>
  );
}

function GlobalAverageRow({ label, global, user }: { label: string; global: number | null; user: number | null }) {
  const toPercent = (v: number) => 50 + Math.max(-50, Math.min(50, v / 2));
  const globalLeft = global !== null ? toPercent(global) : null;
  const userLeft = user !== null ? toPercent(user) : null;
  return (
    <div className="grid items-center gap-3 py-2.5" style={{ gridTemplateColumns: "1fr 1.6fr 5rem" }}>
      <span className="text-[13px]">{label}</span>
      <div className="relative h-[6px] rounded-full" style={{ background: "rgba(30, 41, 59, 0.08)" }}>
        <div className="absolute left-1/2 top-[-5px] h-[16px] w-px" style={{ background: "rgba(30, 41, 59, 0.18)" }} />
        {globalLeft !== null && (
          <div
            className="absolute top-1/2 h-[11px] w-[11px] -translate-x-1/2 -translate-y-1/2 rounded-full transition-all duration-700"
            style={{ left: `${globalLeft}%`, background: "var(--ink)" }}
            aria-label="global average"
          />
        )}
        {userLeft !== null && (
          <div
            className="absolute top-1/2 h-[11px] w-[11px] -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 transition-all duration-700"
            style={{ left: `${userLeft}%`, background: "var(--clay)", boxShadow: "0 0 0 2px var(--paper)" }}
            aria-label="your position"
          />
        )}
      </div>
      <div className="text-right text-[12px] tabular-nums text-[var(--ink-muted)]">
        {global !== null ? `${global > 0 ? "+" : ""}${Math.round(global)}` : "—"}
        {user !== null && (
          <>
            <span className="mx-1 text-[var(--ink-subtle)]">·</span>
            <span style={{ color: "var(--clay)", fontWeight: 500 }}>
              {user > 0 ? "+" : ""}
              {user}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

const PRESSURE_ROWS = [
  "Civil liberty",
  "Order / authority",
  "Economic equality",
  "Institutional checks",
  "Direct democracy",
  "Environmental precaution",
] as const;

const PRESSURE_SCENARIOS: Array<{ label: string; baselines: number[]; deltas: number[] }> = [
  { label: "calm",       baselines: [74, 11, 68, 63, 38, 72], deltas: [-18, +28, +22, -9, +31, +16] },
  { label: "scarcity",   baselines: [62, 22, 74, 56, 47, 65], deltas: [-26, +36, +18, -16, +28, +12] },
  { label: "threat",     baselines: [54, 36, 66, 60, 32, 70], deltas: [-32, +42, +12, -22, +20, +14] },
  { label: "corruption", baselines: [70, 18, 71, 50, 52, 68], deltas: [-12, +26, +16, -28, +38, +18] },
  { label: "complexity", baselines: [66, 24, 64, 58, 41, 67], deltas: [-14, +22, +20, -18, +24, +21] },
];

function PressurePreview() {
  const [scenarioIndex, setScenarioIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setScenarioIndex(i => (i + 1) % PRESSURE_SCENARIOS.length);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  const scenario = PRESSURE_SCENARIOS[scenarioIndex]!;
  return (
    <aside aria-label="Example pressure map" className="self-center">
      <div className="mb-5 flex items-baseline justify-between text-[11px] font-medium uppercase" style={{ letterSpacing: "0.16em", color: "var(--ink-muted)" }}>
        <span>Baseline</span>
        <span>
          Under{" "}
          <span
            key={scenario.label}
            className="text-[var(--ink)]"
            style={{ animation: "fadeSwap 0.6s ease" }}
          >
            {scenario.label}
          </span>
        </span>
      </div>
      <div className="h-px bg-[var(--line-soft)]" />
      {PRESSURE_ROWS.map((name, idx) => {
        const base = scenario.baselines[idx]!;
        const delta = scenario.deltas[idx]!;
        return (
          <div
            key={name}
            className="grid items-center gap-4 border-b border-[var(--line-soft)] py-4"
            style={{ gridTemplateColumns: "1fr 1.4fr 3rem" }}
          >
            <span className="text-[14px] leading-[1.3]">{name}</span>
            <div className="h-[6px] overflow-hidden rounded-full" style={{ background: "rgba(26, 24, 20, 0.08)" }}>
              <span
                className="block h-full rounded-full"
                style={{
                  width: `${Math.abs(base)}%`,
                  background: "var(--ink)",
                  transition: "width 1.6s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              />
            </div>
            <span
              key={`${name}-${scenarioIndex}`}
              className={`text-right tabular-nums text-[14px] ${delta >= 0 ? "delta-plus" : "delta-minus"}`}
              style={{ animation: "fadeSwap 0.6s ease" }}
            >
              {delta > 0 ? "+" : ""}{delta}
            </span>
          </div>
        );
      })}
    </aside>
  );
}

function Questionnaire(props: {
  question: Question;
  answer: AnswerValue | undefined;
  onAnswer: (value: AnswerValue) => void;
  onNext: () => void;
  onBack: () => void;
  index: number;
  total: number;
  progress: number;
  modulePosition: number;
  canBack: boolean;
  onReset: () => void;
}) {
  const q = props.question;
  const answered = props.answer !== undefined;
  return (
    <main className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-16">
      <div className="mb-3 flex items-center justify-between gap-4 text-[13px] text-[var(--ink-muted)]">
        <span className="mono">Question {String(props.index + 1).padStart(2, "0")} / {String(props.total).padStart(2, "0")}</span>
        <button onClick={props.onReset} className="ghost-link">Reset</button>
      </div>
      <div className="progress-track"><span style={{ width: `${props.progress}%` }} /></div>

      <section className="mt-12">
        <p className="eyebrow">
          {q.module}
          {q.type === "tradeoff" && q.stressor && (
            <span className="ml-3 normal-case tracking-normal text-[var(--clay)]">· Pressure test · {q.stressor}</span>
          )}
        </p>

        <h2 className="display mt-4 text-[30px] leading-[1.15] sm:text-[40px]">{q.text}</h2>

        <div className="mt-10">
          {q.type === "likert" && <LikertQuestion question={q} answer={props.answer} onAnswer={props.onAnswer} />}
          {q.type === "tradeoff" && <TradeoffQuestion question={q} answer={props.answer} onAnswer={props.onAnswer} />}
          {q.type === "importance" && <ImportanceQuestion answer={props.answer} onAnswer={props.onAnswer} />}
          {q.type === "multiselect" && <MultiSelectQuestion question={q} answer={props.answer} onAnswer={props.onAnswer} />}
        </div>

        <div className="divider mt-14" />
        <div className="mt-6 flex items-center justify-between gap-3">
          <button className="secondary-button" onClick={props.onBack} disabled={!props.canBack}>← Back</button>
          <button className="primary-button" onClick={props.onNext} disabled={!answered}>
            {props.index === props.total - 1 ? "Show results" : "Continue"} →
          </button>
        </div>
      </section>
    </main>
  );
}

function LikertQuestion({ question, answer, onAnswer }: { question: Extract<Question, { type: "likert" }>; answer: AnswerValue | undefined; onAnswer: (value: AnswerValue) => void }) {
  return (
    <div className="grid gap-2.5">
      {likertLabels.map((label, i) => {
        const value = i + 1;
        const active = answer === value;
        return (
          <button key={label} onClick={() => onAnswer(value)} data-active={active} className="choice focus-ring">
            <span className="badge">{value}</span>
            <span>{label}</span>
          </button>
        );
      })}
      {question.allowNotSure && (
        <button onClick={() => onAnswer("N")} data-active={answer === "N"} className="choice focus-ring">
          <span className="badge">·</span>
          <span>Not sure</span>
        </button>
      )}
    </div>
  );
}

function TradeoffQuestion({ question, answer, onAnswer }: { question: Extract<Question, { type: "tradeoff" }>; answer: AnswerValue | undefined; onAnswer: (value: AnswerValue) => void }) {
  const options = [
    [-3, "Strongly A"], [-2, "A"], [-1, "Slightly A"], [0, "Mixed"], [1, "Slightly B"], [2, "B"], [3, "Strongly B"],
  ] as const;
  return (
    <div>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="option-panel"><span className="marker">A</span><p>{question.optionA}</p></div>
        <div className="option-panel"><span className="marker">B</span><p>{question.optionB}</p></div>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-7">
        {options.map(([value, label]) => (
          <button key={value} onClick={() => onAnswer(value)} data-active={answer === value} className="choice justify-center text-center text-[13px] focus-ring">
            {label}
          </button>
        ))}
      </div>
      {question.allowNotSure && (
        <button onClick={() => onAnswer("N")} data-active={answer === "N"} className="choice mt-2 justify-center focus-ring">Not sure</button>
      )}
    </div>
  );
}

function ImportanceQuestion({ answer, onAnswer }: { answer: AnswerValue | undefined; onAnswer: (value: AnswerValue) => void }) {
  const options = [[0, "Not important / weakly held"], [1, "Somewhat important"], [2, "Important"], [3, "Core belief"]] as const;
  return (
    <div className="grid gap-2.5">
      {options.map(([value, label]) => (
        <button key={value} onClick={() => onAnswer(value)} data-active={answer === value} className="choice focus-ring">
          <span className="badge">{value}</span>
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
}

function MultiSelectQuestion({ question, answer, onAnswer }: { question: Extract<Question, { type: "multiselect" }>; answer: AnswerValue | undefined; onAnswer: (value: AnswerValue) => void }) {
  const selected = Array.isArray(answer) ? answer : [];
  const toggle = (option: string) => {
    if (selected.includes(option)) return onAnswer(selected.filter(x => x !== option));
    if (question.maxSelections === 1) return onAnswer([option]);
    if (question.maxSelections && selected.length >= question.maxSelections) return onAnswer([...selected.slice(1), option]);
    return onAnswer([...selected, option]);
  };
  return (
    <div>
      {question.maxSelections && <p className="mb-4 text-[14px] text-[var(--ink-muted)]">Choose up to {question.maxSelections}.</p>}
      <div className="flex flex-wrap gap-2">
        {question.options.map(option => (
          <button key={option} onClick={() => toggle(option)} data-active={selected.includes(option)} className="pill-choice focus-ring">
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

function Results({
  profile,
  answers,
  scoredAnswered,
  scoredTotal,
  onRestart,
  contribution,
}: {
  profile: ReturnType<typeof scoreQuestionnaire>;
  answers: Answers;
  scoredAnswered: number;
  scoredTotal: number;
  onRestart: () => void;
  contribution: ContributionProfile;
}) {
  const [contribState, setContribState] = useState<ContribState>(loadContribState);
  const [contribStatus, setContribStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const triedRef = useRef(false);

  const respondentIdRef = useRef<string>("");
  if (respondentIdRef.current === "") {
    respondentIdRef.current = loadOrCreateRespondentId();
  }

  useEffect(() => {
    if (triedRef.current) return;
    if (contribState.anonymous) {
      setContribStatus("sent");
      return;
    }
    triedRef.current = true;
    setContribStatus("sending");
    fetch("/api/contribute", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        respondentId: respondentIdRef.current,
        profile: contribution,
        answers: null,
      }),
    })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(() => {
        const next = { ...contribState, anonymous: true };
        setContribState(next);
        saveContribState(next);
        setContribStatus("sent");
      })
      .catch(() => setContribStatus("error"));
  }, [contribState, contribution]);

  const saveFullResponses = () => {
    if (contribState.full) return;
    fetch("/api/contribute", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        respondentId: respondentIdRef.current,
        profile: contribution,
        answers,
      }),
    })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(() => {
        const next = { ...contribState, full: true };
        setContribState(next);
        saveContribState(next);
      })
      .catch(() => {});
  };

  const [chatOpen, setChatOpen] = useState(false);

  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ generatedAt: new Date().toISOString(), answers, profile }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "cvcs-result.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  const slides: Array<{ id: string; title: string; render: () => React.ReactNode }> = [
    {
      id: "cover",
      title: "Overview",
      render: () => (
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
          <div>
            <p className="eyebrow">CVCS pressure profile</p>
            <h1 className="display mt-4 text-[44px] leading-[1.02] sm:text-[60px]">Your pressure map.</h1>
            <p className="mt-6 max-w-xl text-[17px] leading-[1.65] text-[var(--ink-soft)]">
              Six short slides. Each one shows one slice of how you answered. The scores describe resemblance and movement, not identity — low-evidence dimensions are flagged.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <SplitButton
                mainLabel="Chat With My Results"
                onMain={() => setChatOpen(true)}
                menuItems={[{ label: "Export to JSON", onClick: exportJson }]}
              />
              <button className="secondary-button" onClick={onRestart}>Retake</button>
            </div>
          </div>
          <aside className="lg:pt-1">
            <p className="eyebrow">Top match</p>
            <p className="mt-1 text-[13px] text-[var(--ink-muted)]">resemblance, not identity</p>
            {profile.ideologyMatches[0] && (
              <div className="mt-5">
                <h2 className="display text-[28px] leading-[1.1]">{profile.ideologyMatches[0].name}</h2>
                <p className="mt-2 text-[14px] leading-[1.6] text-[var(--ink-muted)]">{profile.ideologyMatches[0].note}</p>
                <p className="mt-3 text-[13px]">
                  <span className="tabular-nums" style={{ color: "var(--clay)", fontWeight: 500 }}>{profile.ideologyMatches[0].percentage}%</span>
                  <span className="ml-2 text-[var(--ink-muted)]">cosine similarity</span>
                </p>
              </div>
            )}
            <p className="mt-6 text-[12px] text-[var(--ink-subtle)]">{scoredAnswered} / {scoredTotal} scored answers</p>
          </aside>
        </div>
      ),
    },
    {
      id: "baseline",
      title: "Baseline coordinates",
      render: () => (
        <div>
          <p className="eyebrow">Baseline coordinates</p>
          <p className="mt-1 text-[13px] text-[var(--ink-muted)]">where you sit on each of the sixteen dimensions, on a −100 to +100 line</p>
          <div className="mt-8 grid gap-5 md:grid-cols-2 md:gap-x-12">
            {dimensionMetas.map(d => <AxisBar key={d.code} label={d.name} value={profile.baseline[d.code].score} evidence={profile.baseline[d.code].evidence} />)}
          </div>
        </div>
      ),
    },
    {
      id: "matches",
      title: "Resemblance",
      render: () => (
        <div className="grid gap-14 lg:grid-cols-[1.2fr_0.8fr]">
          <div>
            <p className="eyebrow">Nearest ideological families</p>
            <p className="mt-1 text-[13px] text-[var(--ink-muted)]">resemblance via cosine similarity — not a label</p>
            <div className="mt-6">
              {profile.ideologyMatches.map(match => <MatchRow key={match.name} {...match} />)}
            </div>
          </div>
          <div>
            <p className="eyebrow">Value anchors</p>
            <p className="mt-1 text-[13px] text-[var(--ink-muted)]">strongest positive signals</p>
            {profile.topValues.length ? (
              <ul className="mt-6">
                {profile.topValues.map(v => (
                  <li key={v.code} className="value-row text-[14px]">
                    <span>{v.name}</span>
                    <b className="tabular-nums">{v.score}</b>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-4 text-[14px] text-[var(--ink-muted)]">No strong value anchors yet.</p>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "pressure",
      title: "Under pressure",
      render: () => (
        <div className="grid gap-14 lg:grid-cols-[1.2fr_0.8fr]">
          <div>
            <p className="eyebrow">Stress deltas</p>
            <p className="mt-1 text-[13px] text-[var(--ink-muted)]">how each dimension shifted in the pressure vignettes vs your baseline</p>
            <div className="mt-8 grid gap-5">
              {dimensionMetas.map(d => <DeltaBar key={d.code} label={d.name} value={profile.stressDeltas[d.code] ?? 0} />)}
            </div>
          </div>
          <div>
            <p className="eyebrow">Pressure categories</p>
            <p className="mt-1 text-[13px] text-[var(--ink-muted)]">grouped movement under stress</p>
            <div className="mt-8 grid gap-5">
              {Object.entries(profile.groupedStress).map(([name, value]) => <DeltaBar key={name} label={name} value={value} />)}
            </div>
            <p className="mt-8 text-[13px] leading-[1.65] text-[var(--ink-muted)]">
              Positive shifts mean stress pulled you toward that response. Negative shifts mean it pulled you away.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "meaning",
      title: "Tensions & meaning",
      render: () => (
        <div className="grid gap-14 lg:grid-cols-2">
          <div>
            <p className="eyebrow">Internal tensions</p>
            <p className="mt-1 text-[13px] text-[var(--ink-muted)]">views standard labels often separate</p>
            {profile.tensions.length ? (
              <div className="mt-8 grid gap-7">
                {profile.tensions.map(t => (
                  <div className="tension-block" key={t.title}>
                    <h3>{t.title}</h3>
                    <p>{t.body}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-6 text-[15px] leading-[1.65] text-[var(--ink-soft)]">No major tension flags from the starter rules. That doesn't mean your views are simple — this version didn't catch a strong off-axis combination.</p>
            )}
          </div>
          <div>
            <p className="eyebrow">What this means</p>
            <p className="mt-1 text-[13px] text-[var(--ink-muted)]">plain-language readout</p>
            <div className="mt-8"><ResultNarrative profile={profile} /></div>
          </div>
        </div>
      ),
    },
    {
      id: "contribute",
      title: "Contribution",
      render: () => (
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
          <div>
            <p className="eyebrow">Contribution</p>
            <h2 className="display mt-3 text-[28px] leading-[1.15]">Your anonymized scores joined the Global Average.</h2>
            <p className="mt-4 max-w-xl text-[15px] leading-[1.7] text-[var(--ink-soft)]">
              Sixteen dimension scores, ten value scores, stress deltas, and the closest ideological family — that's all that gets stored, keyed only by a random respondent ID this browser generated. Status:{" "}
              <span style={{ color: contribStatus === "error" ? "var(--clay)" : "var(--ink)", fontWeight: 500 }}>
                {contribStatus === "sending" && "sending…"}
                {contribStatus === "sent" && "saved"}
                {contribStatus === "error" && "offline — will retry next time"}
                {contribStatus === "idle" && "queued"}
              </span>
              .
            </p>
          </div>
          <div>
            <p className="eyebrow">Optional</p>
            <h3 className="display mt-3 text-[20px]">Can we also save your individual responses?</h3>
            <p className="mt-3 text-[14px] leading-[1.65] text-[var(--ink-muted)]">
              Off by default. If you say yes, we add an anonymous copy of your answers so researchers can study how individual items relate. You can change your mind by retaking the questionnaire.
            </p>
            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  if (contribState.full) {
                    const next = { ...contribState, full: false };
                    setContribState(next);
                    saveContribState(next);
                  }
                }}
                data-active={!contribState.full}
                className="pill-choice focus-ring"
              >
                No, just scores
              </button>
              <button
                type="button"
                onClick={saveFullResponses}
                data-active={contribState.full}
                className="pill-choice focus-ring"
                disabled={contribState.full}
              >
                {contribState.full ? "Yes, saved" : "Yes, include answers"}
              </button>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <main className="mx-auto max-w-6xl px-5 py-10 sm:px-8 sm:py-16">
      <Deck slides={slides} />
      {chatOpen && (
        <ChatModal prompt={buildChatPrompt(answers, profile)} onClose={() => setChatOpen(false)} />
      )}
    </main>
  );
}

function Deck({ slides }: { slides: Array<{ id: string; title: string; render: () => React.ReactNode }> }) {
  const [index, setIndex] = useState(0);
  const total = slides.length;

  const goTo = (n: number) => setIndex(Math.max(0, Math.min(total - 1, n)));

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement && ["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "ArrowRight") goTo(index + 1);
      if (e.key === "ArrowLeft") goTo(index - 1);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [index, total]);

  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [index]);

  const current = slides[index]!;

  return (
    <div className="deck">
      <div key={current.id} className="deck-slide min-h-[60vh]">
        {current.render()}
      </div>

      <nav className="deck-nav mt-12" aria-label="Result slides">
        <button
          type="button"
          className="deck-arrow focus-ring"
          onClick={() => goTo(index - 1)}
          disabled={index === 0}
          aria-label="Previous slide"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
            <path d="M9 3 L5 7 L9 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="deck-progress">
          {slides.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => goTo(i)}
              className={`deck-dot focus-ring ${i === index ? "deck-dot-active" : ""}`}
              aria-label={`Go to ${s.title}`}
              aria-current={i === index ? "true" : undefined}
            />
          ))}
        </div>

        <div className="flex items-center gap-3">
          <span className="deck-label hidden sm:inline">{current.title} · {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}</span>
          <button
            type="button"
            className="deck-arrow focus-ring"
            onClick={() => goTo(index + 1)}
            disabled={index === total - 1}
            aria-label="Next slide"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M5 3 L9 7 L5 11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </nav>
    </div>
  );
}

function SplitButton({
  mainLabel,
  onMain,
  menuItems,
}: {
  mainLabel: string;
  onMain: () => void;
  menuItems: Array<{ label: string; onClick: () => void }>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex">
      <div className="split-button">
        <button type="button" onClick={onMain} className="split-button-main focus-ring">
          {mainLabel}
        </button>
        <span className="split-button-divider" aria-hidden />
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="split-button-toggle focus-ring"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="More options"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
            <path d="M3 4.5 L6 7.5 L9 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      {open && (
        <div role="menu" className="dropdown-menu">
          {menuItems.map(item => (
            <button
              key={item.label}
              role="menuitem"
              type="button"
              className="dropdown-item focus-ring"
              onClick={() => {
                item.onClick();
                setOpen(false);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ChatModal({ prompt, onClose }: { prompt: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = prompt;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); setCopied(true); window.setTimeout(() => setCopied(false), 2000); } catch {}
      document.body.removeChild(ta);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <header className="modal-header">
          <div>
            <p className="eyebrow">Chat with your results</p>
            <h2 className="display mt-2 text-[24px] leading-[1.2]">Paste this into your AI of choice.</h2>
          </div>
          <button type="button" className="modal-close focus-ring" onClick={onClose} aria-label="Close">×</button>
        </header>

        <p className="mt-4 text-[14px] leading-[1.65] text-[var(--ink-muted)]">
          The prompt below briefs the model on what CVCS is, gives it the dimension and value legend, attaches your full profile JSON, and asks for an honest, label-resistant reflection — not a tribe assignment.
        </p>

        <textarea readOnly value={prompt} className="modal-textarea mt-5" spellCheck={false} />

        <footer className="modal-footer">
          <button type="button" className="ghost-link" onClick={onClose}>Close</button>
          <button type="button" className="primary-button" onClick={copy}>
            {copied ? "Copied ✓" : "Copy to clipboard"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function buildChatPrompt(answers: Answers, profile: ReturnType<typeof scoreQuestionnaire>): string {
  const payload = {
    generatedAt: new Date().toISOString(),
    answers,
    profile,
  };
  return `I just completed CVCS — a reflective civic-value instrument that maps political instincts across 16 dimensions and 10 underlying values, then tests how those instincts shift under pressure scenarios (threat, scarcity, corruption, cultural conflict, complexity).

A few things to know before you interpret:
- Scores are on a −100 to +100 scale. They describe resemblance and movement, NOT identity.
- "stressDeltas" show how my scores shift in pressure vignettes versus my baseline. Large positive deltas mean stress pulled me toward that dimension; negative means away.
- "groupedStress" summarizes shifts under Threat / Scarcity / Corruption / Cultural Conflict / Complexity.
- "ideologyMatches" uses cosine similarity against archetype vectors. Treat it as resemblance only — the closest match is not the same as the label being accurate for me.
- "evidence: low" on any axis means it was based on too few answers to take seriously yet.

Dimension legend:
- EE = Economic Equality (redistribution, public provision)
- MA = Market Autonomy (property rights, competition)
- SC = State Capacity (faith in capable public institutions)
- CL = Civil Liberty (speech, privacy, bodily autonomy)
- SP = Social Pluralism (diversity, minority rights)
- OA = Order / Authority (discipline, public order, norms)
- NS = National Solidarity / Boundary (sovereignty, citizenship priority)
- GC = Global Cooperation (international law, cosmopolitan duties)
- IC = Institutional Checks (courts, rule of law, oversight)
- DD = Direct Democracy / Anti-Elite (popular sovereignty)
- ET = Expert Trust / Technocracy (deference to expertise)
- EP = Environmental Precaution (ecological limits, risk restraint)
- CO = Change Openness (experimentation, reform, novelty)
- RA = Structural Attribution (explaining outcomes via systems)
- HI = Hierarchy Acceptance (comfort with stratification)
- LO = Localism / Subsidiarity (local control, decentralization)

Value codes: CARE, EQUALITY, PROPORTION, LIBERTY, LOYALTY, AUTHORITY, PURITY, SECURITY, UNIVERSAL, ACHIEVE.

What I want from you:
1. Read the profile and tell me, in plain candid language, what the combination of scores suggests about how I think about politics — not what label fits, but what the pattern actually reveals.
2. Identify two or three internal tensions or non-obvious combinations that standard political labels would miss in me.
3. Comment on my most notable stress shifts — where my instincts move under pressure, and what that suggests about my actual priorities versus my stated ones.
4. Suggest two or three good follow-up questions worth thinking about based on the gaps or interesting combinations you see.

Please do NOT just hand me a label. CVCS is explicitly anti-label. Treat me as a person who wants honest reflection, not a tribe assignment. Where evidence is low, say so rather than over-reading the number.

Here is my full profile:

\`\`\`json
${JSON.stringify(payload, null, 2)}
\`\`\``;
}

function ResultNarrative({ profile }: { profile: ReturnType<typeof scoreQuestionnaire> }) {
  const top = [...dimensionMetas].sort((a, b) => profile.baseline[b.code].score - profile.baseline[a.code].score).slice(0, 3);
  const biggestShift = Object.entries(profile.groupedStress).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))[0];
  return (
    <div className="space-y-4 text-[15px] leading-[1.75] text-[var(--ink-soft)]">
      <p>Your strongest baseline signals are <b className="text-[var(--ink)]" style={{ fontWeight: 500 }}>{top.map(d => d.name).join(", ")}</b>. Treat these as coordinates: they say where your answers clustered, not what tribe you belong to.</p>
      {biggestShift && <p>Your largest pressure movement is <b className="text-[var(--ink)]" style={{ fontWeight: 500 }}>{biggestShift[0]}</b> ({biggestShift[1] > 0 ? "+" : ""}{biggestShift[1]}). That's the part of the profile most worth rereading: it shows which conditions make priorities shift.</p>}
      <p>The closest family match is <b className="text-[var(--ink)]" style={{ fontWeight: 500 }}>{profile.ideologyMatches[0]?.name ?? "not clear"}</b>, but CVCS reports resemblance because people can be economically egalitarian, culturally cautious, institutionally liberal, and anti-elite at the same time.</p>
    </div>
  );
}

function Methodology({ onStart }: { onStart: () => void }) {
  return (
    <main className="mx-auto max-w-4xl px-5 py-16 sm:px-8 sm:py-24">
      <p className="eyebrow">Methodology · v0.1</p>
      <h1 className="serif mt-4 text-[44px] leading-[1.05] sm:text-[60px]" style={{ fontWeight: 400, letterSpacing: "-0.025em" }}>
        How CVCS scores without pretending to be a lab instrument.
      </h1>

      <div className="mt-12 grid gap-10 text-[16px] leading-[1.75] text-[var(--ink-soft)] md:grid-cols-2">
        <p>CVCS treats ideology as a bundle of dimensions: economic equality, market autonomy, state capacity, liberty, pluralism, order, national boundary, global cooperation, institutional checks, anti-elite democracy, expertise, ecology, change, structure, hierarchy, and localism.</p>
        <p>Likert answers are encoded from −1 to +1. Trade-off answers run from −3 to +3, applying only the side you chose. Section importance lightly weights answers from 0.70 to 1.15, so intensity matters but does not dominate.</p>
        <p>Stress deltas compare baseline scores with the pressure vignettes. Grouped shifts summarize threat, scarcity, corruption, cultural conflict, and complexity. They're prompts for reflection, not moral grades.</p>
        <p>Ideological families use cosine similarity against starter archetype vectors. The app says “you resemble,” never “you are,” because labels hide as much as they reveal.</p>
        <p>A serious validation plan would require cognitive interviews, a 300–500 person pilot, item revision, factor analysis or IRT, test-retest reliability, correlation against known measures, invariance checks, and a published versioned methodology.</p>
        <p>No personal data is sent to a server. Progress stays in browser storage; export is an explicit local download.</p>
      </div>

      <div className="mt-14"><button className="primary-button" onClick={onStart}>Start CVCS →</button></div>
    </main>
  );
}

function describePosition(value: number): { label: string; tone: "clay" | "slate" | "neutral" } {
  const abs = Math.abs(value);
  if (abs <= 10) return { label: "Mixed", tone: "neutral" };
  if (value > 0) {
    if (abs <= 30) return { label: "Leans toward", tone: "clay" };
    if (abs <= 60) return { label: "Favors", tone: "clay" };
    return { label: "Strongly favors", tone: "clay" };
  }
  if (abs <= 30) return { label: "Leans away", tone: "slate" };
  if (abs <= 60) return { label: "Pushes back", tone: "slate" };
  return { label: "Strongly opposes", tone: "slate" };
}

function describeShift(value: number): { label: string; tone: "clay" | "slate" | "neutral" } {
  const abs = Math.abs(value);
  if (abs <= 8) return { label: "Roughly stable", tone: "neutral" };
  const direction = value > 0 ? "toward" : "away";
  const tone = value > 0 ? "clay" : "slate";
  if (abs <= 25) return { label: `Pulls ${direction}`, tone };
  if (abs <= 55) return { label: `Strong pull ${direction}`, tone };
  return { label: `Major shift ${direction}`, tone };
}

const positionPercent = (value: number) =>
  50 + Math.max(-50, Math.min(50, value / 2));

const markerClass = (tone: "clay" | "slate" | "neutral") =>
  tone === "clay" ? "position-marker-clay" : tone === "slate" ? "position-marker-slate" : "position-marker-neutral";

const toneColor = (tone: "clay" | "slate" | "neutral") =>
  tone === "clay" ? "var(--clay)" : tone === "slate" ? "var(--slate)" : "var(--ink-muted)";

function AxisBar({ label, value, evidence }: { label: string; value: number; evidence: "low" | "ok" }) {
  const { label: verdict, tone } = describePosition(value);
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-3 text-[14px]">
        <span>{label}</span>
        <span className="flex items-baseline gap-2 text-[13px]">
          <span style={{ color: toneColor(tone), fontWeight: 500 }}>{verdict}</span>
          <span className="tabular-nums text-[var(--ink-subtle)]">{value > 0 ? "+" : ""}{value}</span>
          {evidence === "low" && <span className="text-[11px] text-[var(--ink-subtle)]">low evidence</span>}
        </span>
      </div>
      <div className="position-track" role="img" aria-label={`${label}: ${verdict} (${value > 0 ? "+" : ""}${value})`}>
        <div className={`position-marker ${markerClass(tone)}`} style={{ left: `${positionPercent(value)}%` }} />
      </div>
    </div>
  );
}

function DeltaBar({ label, value }: { label: string; value: number }) {
  const { label: verdict, tone } = describeShift(value);
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-3 text-[14px]">
        <span>{label}</span>
        <span className="flex items-baseline gap-2 text-[13px]">
          <span style={{ color: toneColor(tone), fontWeight: 500 }}>{verdict}</span>
          <span className="tabular-nums text-[var(--ink-subtle)]">{value > 0 ? "+" : ""}{value}</span>
        </span>
      </div>
      <div className="position-track" role="img" aria-label={`${label}: ${verdict} (${value > 0 ? "+" : ""}${value})`}>
        <div className={`position-marker ${markerClass(tone)}`} style={{ left: `${positionPercent(value)}%` }} />
      </div>
    </div>
  );
}

function MatchRow({ name, percentage, note }: { name: string; percentage: number; note: string }) {
  return (
    <div className="match-row">
      <div className="min-w-0 flex-1">
        <h3>{name}</h3>
        <p>{note}</p>
      </div>
      <span className="pct shrink-0">{percentage}%</span>
    </div>
  );
}

function Privacy({ onBack }: { onBack: () => void }) {
  return (
    <main className="mx-auto max-w-3xl px-5 py-16 sm:px-8 sm:py-24">
      <p className="eyebrow">Privacy policy</p>
      <h1 className="display mt-4 text-[44px] leading-[1.05] sm:text-[56px]">How CVCS handles your data.</h1>
      <p className="mt-6 text-[17px] leading-[1.65] text-[var(--ink-soft)]">
        Short version: when you finish, an anonymized copy of your scores joins the Global Average. We never collect anything that could identify you. Saving your individual answers is opt-in only and off by default.
      </p>

      <div className="mt-14 grid gap-12">
        <PrivacySection title="What we don't collect">
          <p>No name, email, IP address, location, or device fingerprint. There is no account, no login, and no cookie that ties scores back to you.</p>
        </PrivacySection>

        <PrivacySection title="Your respondent ID">
          <p>
            On first launch, your browser generates a random UUID and stores it in localStorage as your <em>respondent ID</em>. This ID is sent with each contribution so the aggregate can de-duplicate — if the same browser submits twice (refresh, retake, hot-reload, etc.), the new scores <em>replace</em> the old ones instead of double-counting.
          </p>
          <p className="mt-3">
            The ID is a random string we never tie to anything about you. Clearing your browser storage wipes it; using a different browser or private window generates a new one. It exists to keep the Global Average honest, not to track you.
          </p>
        </PrivacySection>

        <PrivacySection title="What we store by default">
          <p>
            When you reach the results page, your final scores — sixteen dimension scores, ten value scores, stress deltas, grouped pressure shifts, and the name of your top ideological-family match — are saved server-side under your respondent ID. The shared aggregate file stores only running <em>sums and counts</em>, so an individual contribution cannot be backed out of it.
          </p>
          <p className="mt-3">
            Nothing else is stored by default: not your individual answers, not a session identifier, not a timestamp tied to anything personal.
          </p>
        </PrivacySection>

        <PrivacySection title="Why we store it">
          <p>
            To compute the Global Average. The results page and the home page both show your profile relative to the running aggregate, so you can see how you compare to everyone else who has finished. The arithmetic is deterministic: the average is just sum ÷ count for each axis, which gives the same answer regardless of who contributed first.
          </p>
        </PrivacySection>

        <PrivacySection title="Optional: saving your individual responses">
          <p>
            At the bottom of your results page, you can choose to also save your <em>individual answers</em> — the raw 1–7 likert values, trade-off picks, and multiselect choices. This option is off by default, has no identifier attached, and is intended only for researchers studying item-level patterns. You can change your mind by retaking and choosing "No."
          </p>
        </PrivacySection>

        <PrivacySection title="What we will never do">
          <ul className="mt-2 grid gap-2 text-[15px] leading-[1.7] text-[var(--ink-soft)]">
            <li>— Sell, share, or transfer your data to a third party.</li>
            <li>— Attempt to de-anonymize aggregate or opt-in data.</li>
            <li>— Track you across sessions or across other websites.</li>
            <li>— Use your answers to train models.</li>
          </ul>
        </PrivacySection>

        <PrivacySection title="Your local copy">
          <p>While you're taking the questionnaire, your progress lives in your browser's localStorage so you can come back and finish later. The Reset button on any question wipes it. Exporting JSON downloads a copy to your machine; nothing about the export is logged.</p>
        </PrivacySection>

        <PrivacySection title="Questions or takedowns">
          <p>Because nothing is tied to you in the first place, there is nothing for us to delete on request. If something here feels wrong, open an issue on the project repository and it will be addressed publicly.</p>
        </PrivacySection>
      </div>

      <div className="mt-16 flex flex-wrap items-center gap-3">
        <button className="primary-button" onClick={onBack}>Back to CVCS</button>
        <span className="text-[13px] text-[var(--ink-muted)]">Last updated: {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
      </div>
    </main>
  );
}

function PrivacySection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="display text-[22px]" style={{ fontWeight: 500 }}>{title}</h2>
      <div className="mt-3 text-[15px] leading-[1.75] text-[var(--ink-soft)]">{children}</div>
    </section>
  );
}

export default App;
